import { px } from '@adsk/alloy-react-helpers';

import {
  AlloyLegacyThemeDesignTokens,
  StepIndicatorDesignTokens,
} from '../types';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;

export const getStepIndicatorTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): StepIndicatorDesignTokens => ({
  node: {
    'border-width': '2px',
    'border-radius': theme.borderRadius.circle,
    'font-family': theme.typography.labelMedium.fontFamily,
    'font-size': theme.typography.labelMedium.fontSize,
    'border-color': {
      default: theme.tokens.colors.border.inactive.value,
      selected: theme.tokens.colors.border.information.value,
    },
    'text-color': {
      default: theme.tokens.colors.icon.inactive.value,
      selected: theme.tokens.colors.icon.information.value,
    },
    'background-color': {
      complete: theme.tokens.colors.background.information.value,
    },
    /**
     * Alloy draws the complete state as a filled check icon shrunk by an inset
     * shadow. Weave instead fills the node via `background-color.complete`, so
     * its factory sets this to `undefined` (documented there).
     */
    complete: {
      'box-shadow': `inset 0 0 0 4px ${theme.tokens.colors.background.information.value}`,
    },
    /** Weave-only interactive halos; Alloy has no node halo (see `interactive`). */
    'box-shadow': {
      hover: undefined,
      focus: undefined,
      pressed: undefined,
    },
  },
  label: {
    'font-family': theme.typography.labelMedium.fontFamily,
    'font-size': theme.typography.labelMedium.fontSize,
    'line-height': theme.typography.labelMedium.lineHeight,
    'text-color': {
      default: theme.tokens.colors.text.secondary.value,
      selected: theme.tokens.colors.text.primary.value,
    },
    /**
     * Alloy keeps a constant label weight across states — only Weave shifts the
     * weight between `default` and `selected`. `default` and `selected` are
     * intentionally identical here; do NOT "fix" this into per-state weights,
     * it is a deliberate Alloy/Weave parity boundary.
     */
    'font-weight': {
      default: theme.typography.labelMedium.fontWeight,
      selected: theme.typography.labelMedium.fontWeight,
    },
  },
  connector: {
    'border-width': '2px',
    'border-color': {
      default: theme.tokens.colors.border.inactive.value,
      complete: theme.tokens.colors.border.information.value,
    },
  },
  spacing: {
    'label-gap': px(theme.spacing.XS),
    'row-padding-vertical': px(theme.spacing.M),
    'child-padding-left': px(theme.spacing.XL),
    'child-padding-bottom': px(theme.spacing.M),
    'chevron-gap': px(theme.spacing.XS),
  },
  /**
   * Intentionally undefined for Alloy. The pre-migration component interpolated
   * `getInteractiveStyles(false)` (a style object) into a styled-components
   * template literal, which stringified to `[object Object]` and produced no
   * CSS — Alloy advanced steps never actually rendered a hover/focus/active
   * halo. Leaving `transition` (and the node `box-shadow` halos) undefined
   * preserves that behavior; the component gates halos on token presence via
   * `hasInteractiveHalo`, so only Weave applies them.
   */
  interactive: {
    transition: undefined,
  },
});

export const getStepIndicatorTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): StepIndicatorDesignTokens => {
  const stepIndicator = tokens?.component?.stepindicator;
  const node = stepIndicator?.node;
  const label = stepIndicator?.label;
  const connector = stepIndicator?.connector;

  return {
    node: {
      'border-width': node?.['border-width'],
      'border-radius': node?.['border-radius'],
      'font-family': fontFamily,
      'font-size': node?.['font-size'],
      'border-color': {
        default: node?.['border-color']?.default,
        selected: node?.['border-color']?.selected,
      },
      'text-color': {
        default: node?.['text-color']?.default,
        selected: node?.['text-color']?.selected,
      },
      'background-color': {
        complete: node?.['background-color']?.complete,
      },
      /**
       * Weave fills the complete node via `background-color.complete` and needs
       * no inset shadow, so this is intentionally undefined (the Alloy factory
       * emits a real inset shadow here). Keeps the key set symmetric.
       */
      complete: {
        'box-shadow': undefined,
      },
      'box-shadow': {
        hover: node?.['box-shadow']?.hover,
        focus: node?.['box-shadow']?.focus,
        pressed: node?.['box-shadow']?.pressed,
      },
    },
    label: {
      'font-family': fontFamily,
      'font-size': label?.['font-size'],
      'line-height': label?.['line-height'],
      /**
       * The Weave `label.text-color` token is a flat `string` (Weave changes
       * only the label weight between states, not its color), so both `default`
       * and `selected` map to that same value. Do NOT change these to
       * `label?.['text-color']?.default` — the token is not an object and that
       * would always be `undefined`.
       */
      'text-color': {
        default: label?.['text-color'],
        selected: label?.['text-color'],
      },
      'font-weight': {
        default: label?.['font-weight']?.default,
        selected: label?.['font-weight']?.selected,
      },
    },
    connector: {
      'border-width': connector?.['border-width'],
      'border-color': {
        default: connector?.['border-color']?.default,
        complete: connector?.['border-color']?.complete,
      },
    },
    spacing: {
      'label-gap': tokens?.generic?.spacing?.[8],
      'row-padding-vertical': tokens?.generic?.spacing?.[16],
      'child-padding-left': stepIndicator?.child?.['padding-left'],
      'child-padding-bottom': stepIndicator?.child?.['padding-bottom'],
      'chevron-gap': tokens?.generic?.spacing?.[8],
    },
    interactive: {
      transition:
        'box-shadow 200ms cubic-bezier(0.2, 0, 0.2, 1), border-color 200ms cubic-bezier(0.2, 0, 0.2, 1)',
    },
  };
};
